/**
 * User management types
 */

import type { Salutation } from './salutation'

// Role categories
export type RoleCategory = 'Administrative' | 'Clinical' | 'System'

// Specific roles mapped to categories
export type UserRole =
  // Clinical roles
  | 'doctor'
  | 'nurse'
  | 'physician-assistant'
  | 'therapist'
  // Administrative roles
  | 'admin'
  | 'receptionist'
  | 'billing'
  | 'office-manager'
  // System roles
  | 'system-admin'
  | 'it-support'

export interface User {
  id: string
  /**
   * Username used for login (may be distinct from email)
   */
  username: string
  email: string
  /**
   * Optional salutation/title (e.g. Dr, Prof, Mr, Ms)
   */
  salutation?: Salutation
  firstName: string
  middleName?: string
  lastName: string
  role: UserRole
  roleCategory: RoleCategory // Categorization for filtering
  permissions?: string[]
  isActive: boolean
  createdAt: string
  updatedAt: string
  lastLoginAt?: string
  // Contact information
  phone?: string
  mobile?: string
  address?: {
    street?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  // Professional information
  hpi?: string // Healthcare Practitioner Identifier
  professionalBodyRegistrationNumber?: string
  prescriberIdentifierNumber?: string
  gmsNumber?: string // Doctor's GMS number
  // Contractual arrangements
  contractualArrangements?: Array<{
    type: 'full-time' | 'part-time'
    startDate: string
    endDate?: string
  }>
  // Signature S3 key (signatures stored in S3, key stored in Cognito)
  signatureS3Key?: string
  // Emergency override capability (stored as metadata, actual permission check uses 'patients:override:emergency')
  canOverrideAccess?: boolean // Deprecated: use hasPermission('patients:override:emergency') instead
}

export interface UserFormData {
  username: string
  email: string
  /**
   * Optional salutation/title (e.g. Dr, Prof, Mr, Ms)
   */
  salutation?: Salutation
  firstName: string
  middleName?: string
  lastName: string
  role: UserRole
  roleCategory: RoleCategory
  permissions?: string[]
  password?: string
  isActive: boolean
  // Contact information
  phone?: string
  mobile?: string
  address?: {
    street?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  // Professional information
  hpi?: string // Healthcare Practitioner Identifier
  professionalBodyRegistrationNumber?: string
  prescriberIdentifierNumber?: string
  gmsNumber?: string // Doctor's GMS number
  // Contractual arrangements
  contractualArrangements?: Array<{
    type: 'full-time' | 'part-time'
    startDate: string
    endDate?: string
  }>
  // Signature S3 key (signatures stored in S3, key stored in Cognito)
  signatureS3Key?: string
}

// Helper function to get role category from role
export function getRoleCategory(role: UserRole): RoleCategory {
  const clinicalRoles: UserRole[] = ['doctor', 'nurse', 'physician-assistant', 'therapist']
  const systemRoles: UserRole[] = ['system-admin', 'it-support']
  
  if (clinicalRoles.includes(role)) return 'Clinical'
  if (systemRoles.includes(role)) return 'System'
  return 'Administrative'
}

// Helper function to check if role is clinical
export function isClinicalRole(role: UserRole): boolean {
  return getRoleCategory(role) === 'Clinical'
}

